import { useCallback, useRef, useState } from 'react';

/**
 * * Hook that uses ResizeObserver to detect if an element's content is overflowing.
 * Returns a ref to attach to an element and a boolean indicating whether
 * the element's content is currently clipped (scrollWidth > clientWidth).
 *
 * Usage:
 *   const [ref, isOverflowing] = useIsOverflowing<HTMLSpanElement>();
 *   <Tooltip disableHoverListener={!isOverflowing}>
 *     <span ref={ref}>...</span>
 *   </Tooltip>
 */
function useIsOverflowing<T extends HTMLElement>(): [
  (node: T | null) => void,
  boolean
] {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    // Disconnect previous observer if any
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    const check = () => {
      setIsOverflowing(node.scrollWidth > node.clientWidth);
    };

    // Check immediately and also on resize (e.g. window resize, panel resize)
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, isOverflowing];
}

export default useIsOverflowing;
